import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChatLayout,
  Viewport,
  ajustarLayout,
  lerLayoutSalvo,
  salvarLayout,
} from '../components/Chat/chatLayout';

const viewportAtual = (): Viewport => ({ width: window.innerWidth, height: window.innerHeight });
const ESPERA_PARA_SALVAR = 200;

export interface ChatLayoutPrefs {
  layout: ChatLayout;
  moverPara: (position: ChatLayout['position']) => void;
  redimensionarPara: (size: ChatLayout['size']) => void;
  /** Posição e tamanho de uma vez — resize pelas bordas esquerda/topo. */
  ajustarCaixa: (caixa: Pick<ChatLayout, 'position' | 'size'>) => void;
  redimensionarLista: (largura: number) => void;
  alternarLista: () => void;
}

/**
 * Posição, tamanho e largura da lista do popup do chat, guardados por usuário.
 *
 * Só o que o usuário faz (arrastar, redimensionar, colapsar) é gravado. O
 * reencaixe automático numa janela menor muda o que aparece na tela mas NÃO
 * sobrescreve a preferência: senão abrir o app numa tela pequena apagava para
 * sempre o tamanho escolhido na tela grande.
 */
export function useChatLayoutPrefs(userId: string | null | undefined): ChatLayoutPrefs {
  const [layout, setLayout] = useState<ChatLayout>(() => lerLayoutSalvo(userId, viewportAtual()));
  const layoutRef = useRef(layout);
  const carregadoPara = useRef<string | null | undefined>(userId);

  // Gravação com debounce: arrastar dispara dezenas de atualizações por segundo.
  const pendenteRef = useRef<ChatLayout | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const descarregarPendente = useCallback((id: string | null | undefined) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (pendenteRef.current) {
      salvarLayout(id, pendenteRef.current);
      pendenteRef.current = null;
    }
  }, []);

  const agendarGravacao = useCallback((novo: ChatLayout) => {
    pendenteRef.current = novo;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (pendenteRef.current) salvarLayout(carregadoPara.current, pendenteRef.current);
      pendenteRef.current = null;
      timerRef.current = null;
    }, ESPERA_PARA_SALVAR);
  }, []);

  const aplicar = useCallback(
    (mudanca: Partial<ChatLayout>) => {
      const novo = ajustarLayout({ ...layoutRef.current, ...mudanca }, viewportAtual());
      layoutRef.current = novo;
      setLayout(novo);
      agendarGravacao(novo);
    },
    [agendarGravacao]
  );

  // Login ou troca de conta: grava o que ficou pendente da conta anterior e
  // carrega o layout de quem está logado agora.
  useEffect(() => {
    if (carregadoPara.current === userId) return;
    descarregarPendente(carregadoPara.current);
    carregadoPara.current = userId;
    const doUsuario = lerLayoutSalvo(userId, viewportAtual());
    layoutRef.current = doUsuario;
    setLayout(doUsuario);
  }, [userId, descarregarPendente]);

  // Janela do navegador diminuiu: reencaixa o popup sem tocar no que está salvo.
  useEffect(() => {
    const aoRedimensionarJanela = () => {
      const encaixado = ajustarLayout(layoutRef.current, viewportAtual());
      layoutRef.current = encaixado;
      setLayout(encaixado);
    };
    window.addEventListener('resize', aoRedimensionarJanela);
    return () => window.removeEventListener('resize', aoRedimensionarJanela);
  }, []);

  // Fechar a aba ou desmontar no meio do debounce não pode perder o ajuste.
  useEffect(() => {
    const aoSair = () => descarregarPendente(carregadoPara.current);
    window.addEventListener('beforeunload', aoSair);
    return () => {
      window.removeEventListener('beforeunload', aoSair);
      descarregarPendente(carregadoPara.current);
    };
  }, [descarregarPendente]);

  return {
    layout,
    moverPara: useCallback((position: ChatLayout['position']) => aplicar({ position }), [aplicar]),
    redimensionarPara: useCallback((size: ChatLayout['size']) => aplicar({ size }), [aplicar]),
    ajustarCaixa: useCallback(
      (caixa: Pick<ChatLayout, 'position' | 'size'>) => aplicar(caixa),
      [aplicar]
    ),
    redimensionarLista: useCallback(
      (larguraDaLista: number) => aplicar({ larguraDaLista }),
      [aplicar]
    ),
    alternarLista: useCallback(
      () => aplicar({ listaColapsada: !layoutRef.current.listaColapsada }),
      [aplicar]
    ),
  };
}
